import { AnyAction } from 'redux'
import { AuthTokens } from 'src/queries/tokenStore'
import {
  CONFIRM_COGNITO_SIGNIN_SUCCESS,
  DELETE_COGNITO_SIGNOUT_SUCCESS,
  DELETE_SIGNOUT_SUCCESS,
  GET_HEARTBEAT_FAILURE,
  POPULATE_USER_INFO_LEGACY,
  POST_AMPLIFY_SIGNIN_INIT,
  POST_AMPLIFY_SIGNIN_SUCCESS,
  POST_COGNITO_RENEW_AUTH_SUCCESS,
  POST_RENEW_AUTH_SUCCESS,
  POST_SIGNIN_INIT,
  POST_SIGNIN_SUCCESS,
} from 'src/redux/actions/auth.actions'

// Auth0 flows require a non-undefined refresh token to stay live; Amplify v6 holds the
// real refresh token internally, so cognito login writes this sentinel. Byte-identical
// to the literal the reducer sets.
const AMPLIFY_REFRESH_SENTINEL = 'HANDLED_INTERNALLY_BY_AMPLIFY_V6'

/** Authoritative token-write logic mirroring auth.reducer's login/renew/heartbeat/signout cases. See src/queries/AGENTS.md for the write-parity invariant. */
export type TokenWrite = (prev: AuthTokens) => AuthTokens

/** True when every token field is undefined (routes signout through `clearTokens`). */
export const areTokensEmpty = (t: AuthTokens): boolean =>
  t.jwt === undefined &&
  t.refreshToken === undefined &&
  t.cspJwt === undefined &&
  t.cspRefreshToken === undefined &&
  t.userSessionTokenId === undefined

const actionIsCsp = (action: AnyAction): boolean =>
  Boolean((action as { payload?: { isCsp?: boolean } }).payload?.isCsp)

type RenewPayload = { token: string; refreshToken?: string; isCsp?: boolean }

// Renew / refresh. Mirrors auth.reducer POST_(COGNITO_)RENEW_AUTH_SUCCESS: rotate the
// active session's jwt; only overwrite the refresh token when the payload carries one
// (cognito renew may omit it — Amplify holds it internally).
const renewSession: (payload: RenewPayload) => TokenWrite =
  ({ token, refreshToken, isCsp }) =>
  (prev) =>
    isCsp
      ? {
          ...prev,
          cspJwt: token,
          ...(refreshToken !== undefined
            ? { cspRefreshToken: refreshToken }
            : {}),
        }
      : {
          ...prev,
          jwt: token,
          ...(refreshToken !== undefined ? { refreshToken } : {}),
        }

// Signout / session teardown. Mirrors auth.reducer DELETE_(COGNITO_)SIGNOUT_SUCCESS:
// always clears the primary trio, and the CSP pair too when the teardown is a CSP one.
const clearSession: (isCsp: boolean) => TokenWrite = (isCsp) => (prev) => ({
  ...prev,
  jwt: undefined,
  refreshToken: undefined,
  userSessionTokenId: undefined,
  ...(isCsp ? { cspJwt: undefined, cspRefreshToken: undefined } : {}),
})

// Heartbeat failure. Mirrors auth.reducer GET_HEARTBEAT_FAILURE: the CSP branch clears
// only the CSP pair; otherwise the primary trio.
const clearOnHeartbeatFailure: (isCsp: boolean) => TokenWrite =
  (isCsp) => (prev) =>
    isCsp
      ? { ...prev, cspJwt: undefined, cspRefreshToken: undefined }
      : {
          ...prev,
          jwt: undefined,
          refreshToken: undefined,
          userSessionTokenId: undefined,
        }

// login/password (POST_SIGNIN_SUCCESS). Mirrors the reducer: jwt + refreshToken from
// the payload.
const loginWithToken: (payload: {
  token: string
  refreshToken: string
}) => TokenWrite =
  ({ token, refreshToken }) =>
  (prev) => ({ ...prev, jwt: token, refreshToken })

// login/cognito (POST_AMPLIFY_SIGNIN_SUCCESS, CONFIRM_COGNITO_SIGNIN_SUCCESS). Mirrors
// the reducer: only a defined jwt is a real login — set jwt + the Amplify sentinel;
// otherwise leave the tokens untouched (the action still flips non-token MFA flags).
const loginWithAmplifyJwt: (jwt: string | undefined) => TokenWrite =
  (jwt) => (prev) =>
    jwt !== undefined
      ? { ...prev, jwt, refreshToken: AMPLIFY_REFRESH_SENTINEL }
      : prev

// login/legacy (POPULATE_USER_INFO_LEGACY). Mirrors the reducer: jwt from the payload +
// the Amplify sentinel.
const loginLegacy: (jwt: string) => TokenWrite = (jwt) => (prev) => ({
  ...prev,
  jwt,
  refreshToken: AMPLIFY_REFRESH_SENTINEL,
})

// login init (POST_SIGNIN_INIT, POST_AMPLIFY_SIGNIN_INIT). Mirrors the reducer: clear
// only the primary jwt.
const clearJwt: TokenWrite = (prev) => ({ ...prev, jwt: undefined })

export const resolveTokenWrite = (
  action: AnyAction,
): TokenWrite | undefined => {
  switch (action.type) {
    case DELETE_SIGNOUT_SUCCESS:
    case DELETE_COGNITO_SIGNOUT_SUCCESS:
      return clearSession(actionIsCsp(action))
    case GET_HEARTBEAT_FAILURE:
      return clearOnHeartbeatFailure(actionIsCsp(action))
    case POST_RENEW_AUTH_SUCCESS:
    case POST_COGNITO_RENEW_AUTH_SUCCESS:
      return renewSession(
        (action as unknown as { payload: RenewPayload }).payload,
      )
    case POST_SIGNIN_SUCCESS:
      return loginWithToken(
        (
          action as unknown as {
            payload: { token: string; refreshToken: string }
          }
        ).payload,
      )
    case POST_AMPLIFY_SIGNIN_SUCCESS:
    case CONFIRM_COGNITO_SIGNIN_SUCCESS:
      return loginWithAmplifyJwt(
        (action as unknown as { payload: { jwt: string | undefined } }).payload
          .jwt,
      )
    case POPULATE_USER_INFO_LEGACY:
      return loginLegacy(
        (action as unknown as { payload: { jwt: string } }).payload.jwt,
      )
    case POST_SIGNIN_INIT:
    case POST_AMPLIFY_SIGNIN_INIT:
      return clearJwt
    default:
      return undefined
  }
}
